import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type Driver, type DriveStep } from 'driver.js'

import { useSettingsStore } from '~/stores/settings'

import { resolveTourResumeStepIndex } from '../resolve-tour-resume-step-index'
import { createOnboardingTourDriver } from '../shared/create-tour-driver'
import { TourFinishConfirmDrawer } from '../shared/tour-finish-confirm-drawer'

import { createCardsTourSteps } from './steps-factory'

import 'driver.js/dist/driver.css'
import '../shared/driver-tour-overrides.css'

const WAIT_FOR_LAYOUT_MS = 300

export const CardsTour = () => {
  const { t } = useTranslation(['onboarding', 'translation'], { nsMode: 'fallback' })
  const toursDisabled = useSettingsStore(state => state.toursDisabled)
  const cardsTour = useSettingsStore(state => state.tours.cards)
  const disableTours = useSettingsStore(state => state.disableTours)
  const [isFinishConfirmOpen, setIsFinishConfirmOpen] = useState(false)
  const driverInstanceRef = useRef<Driver | null>(null)
  const resumeStepRef = useRef<DriveStep | null>(null)
  const isEndingTourRef = useRef(false)

  const shouldStart = !toursDisabled && !cardsTour.completed

  const handleKeepExploring = () => {
    if (isEndingTourRef.current) return

    setIsFinishConfirmOpen(false)
    const resumeStep = resumeStepRef.current
    if (resumeStep && driverInstanceRef.current) driverInstanceRef.current.highlight(resumeStep)
  }

  const handleEndTour = () => {
    isEndingTourRef.current = true
    setIsFinishConfirmOpen(false)
    disableTours()
    driverInstanceRef.current?.destroy()
    driverInstanceRef.current = null
  }

  useEffect(() => {
    if (!shouldStart) return

    const driverObj = createOnboardingTourDriver({
      nextBtnText: t('Next'),
      onCloseClick: () => {
        resumeStepRef.current = driverObj.getActiveStep() ?? null
        driverObj.destroy()
        setIsFinishConfirmOpen(true)
      },
    })
    driverInstanceRef.current = driverObj

    const { highlightStep, steps } = createCardsTourSteps({
      driverObj,
      onTourCompleted: () => {
        driverObj.destroy()
        driverInstanceRef.current = null
      },
      t,
    })

    const resumeStepIndex = resolveTourResumeStepIndex(steps.length, cardsTour.stepIndex)
    if (resumeStepIndex == null) {
      driverObj.destroy()
      driverInstanceRef.current = null
      return
    }

    const startStep = steps[resumeStepIndex]
    if (!startStep) {
      driverObj.destroy()
      driverInstanceRef.current = null
      return
    }

    const layoutTimeoutId = setTimeout(() => {
      highlightStep(startStep, resumeStepIndex)
    }, WAIT_FOR_LAYOUT_MS)

    return () => {
      clearTimeout(layoutTimeoutId)
      driverObj.destroy()
      driverInstanceRef.current = null
    }
    // Resume from persisted stepIndex only on mount; avoid restarting when stepIndex advances mid-tour.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-once for driver lifecycle
  }, [shouldStart, t])

  if (!shouldStart) return null

  return (
    <TourFinishConfirmDrawer
      onClose={handleKeepExploring}
      onEndTour={handleEndTour}
      onKeepExploring={handleKeepExploring}
      open={isFinishConfirmOpen}
    />
  )
}
